import * as React from 'react'
import { Icon, IconName } from '@blueprintjs/core'
import classNames from 'classnames'

/**
 * Drop-in replacement for `Callout` from `@blueprintjs/core`.
 *
 * Matches Blueprint's API surface so callsites can switch by changing the
 * import. Visuals match Blueprint's stock styling — same translucent
 * background per intent, same colors, same icon positioning, same font size
 * and line-height. Values copied verbatim from blueprint.css.
 *
 * Supported props:
 *   - intent           → 'none' | 'primary' | 'success' | 'warning' | 'danger'
 *                        (also accepts 'light' / 'secondary' as no-intent
 *                        for parity with codebase usage; Blueprint silently
 *                        ignores those too)
 *   - title            → ReactNode rendered as an h4 heading at the top
 *   - icon             → IconName (string) or ReactElement; positioned
 *                        absolute at top-left when present
 *   - className/style  → forwarded to the wrapper
 *   - all standard <div> HTML attributes via spread
 *
 * Usage audit (2026-05-07): 22 files, 34 <Callout> tags. Real props:
 * `intent` (34, every callout has one), `className` (26), `title` (14),
 * `icon` (14 — string names, ternary expressions, and JSX), `style` (2),
 * `onClick` (2). All covered.
 *
 * Note: this component still imports Blueprint's `Icon` since Icon itself
 * hasn't been migrated. When that migration ships, this file's Icon import
 * will get swept along automatically.
 */

type Intent = 'none' | 'primary' | 'success' | 'warning' | 'danger' | 'light' | 'secondary'

export interface CalloutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    intent?: Intent
    title?: React.ReactNode
    icon?: IconName | React.ReactElement | false | null
}

// Background + text colors copied verbatim from `.bp4-callout.bp4-intent-X`
// in node_modules/@blueprintjs/core/lib/css/blueprint.css.
const intentClasses: Record<Intent, string> = {
    none: 'bg-[rgba(143,153,168,0.15)]',
    light: 'bg-[rgba(143,153,168,0.15)]',
    secondary: 'bg-[rgba(143,153,168,0.15)]',
    primary: 'bg-[rgba(45,114,210,0.1)] text-[#215db0]',
    success: 'bg-[rgba(35,133,81,0.1)] text-[#1c6e42]',
    warning: 'bg-[rgba(200,118,25,0.1)] text-[#935610]',
    danger: 'bg-[rgba(205,66,70,0.1)] text-[#ac2f33]',
}

// Icon color per intent. None/light/secondary use Blueprint's default
// muted gray (#5f6b7c). Other intents inherit `currentColor` from the
// wrapper which already has the correct intent text color.
const iconColorClass: Record<Intent, string> = {
    none: 'text-[#5f6b7c]',
    light: 'text-[#5f6b7c]',
    secondary: 'text-[#5f6b7c]',
    primary: 'text-current',
    success: 'text-current',
    warning: 'text-current',
    danger: 'text-current',
}

// Heading color matches the intent (Blueprint sets this via
// `.bp4-callout.bp4-intent-X .bp4-heading`).
const headingColorClass: Record<Intent, string> = {
    none: 'text-[#1c2127]',
    light: 'text-[#1c2127]',
    secondary: 'text-[#1c2127]',
    primary: 'text-current',
    success: 'text-current',
    warning: 'text-current',
    danger: 'text-current',
}

// Blueprint auto-picks an icon when an intent is set but no `icon` prop is
// provided. Pass `icon={null}` or `icon={false}` to suppress.
const defaultIntentIcon: Partial<Record<Intent, IconName>> = {
    primary: 'info-sign',
    success: 'tick',
    warning: 'warning-sign',
    danger: 'error',
}

const Callout = React.forwardRef<HTMLDivElement, CalloutProps>(function Callout(
    { intent = 'none', title, icon, className, children, ...rest },
    ref,
) {
    // Resolve the icon: explicit false/null suppresses, undefined falls back
    // to the intent default (matches Blueprint behavior).
    const explicitlySuppressed = icon === false || icon === null
    const resolvedIcon = explicitlySuppressed
        ? null
        : icon === undefined
          ? (defaultIntentIcon[intent] ?? null)
          : icon
    const hasIcon = resolvedIcon != null
    const iconNode = hasIcon ? (
        React.isValidElement(resolvedIcon) ? (
            resolvedIcon
        ) : (
            <Icon icon={resolvedIcon as IconName} size={16} />
        )
    ) : null

    return (
        <div
            ref={ref}
            className={classNames(
                // Base: matches `.bp4-callout` — 14px font, 1.5 line-height,
                // 2px border-radius, 15px padding, 100% width, relative
                // positioning (so the absolute icon anchors here).
                'relative w-full text-[14px] leading-[1.5] rounded-sm',
                hasIcon ? 'pl-[38px] pr-[15px] py-[15px]' : 'p-[15px]',
                intentClasses[intent],
                className,
            )}
            {...rest}
        >
            {hasIcon && (
                // Matches `.bp4-callout.bp4-callout-icon > .bp4-icon:first-child`:
                // absolute, top:17px, left:15px, intent-colored.
                <span
                    className={classNames(
                        'absolute left-[15px] top-[17px]',
                        iconColorClass[intent],
                    )}
                >
                    {iconNode}
                </span>
            )}
            {title != null && (
                // Matches Blueprint's <h5 class="bp4-heading"> inside callouts.
                // Blueprint's CalloutComponent actually renders an <h5>, not
                // <h4> (verified by inspecting the live DOM). Stock
                // h5.bp4-heading is 16px / 19px line-height, but
                // `.bp4-callout .bp4-heading` (compound selector, higher
                // specificity) overrides to line-height: 16px and margin:
                // 2px 0 5px 0. Using h4 with 18px here made the title look
                // visibly bigger than Blueprint's — both the size AND the
                // tag matter.
                <h5
                    className={classNames(
                        'text-[16px] leading-[16px] font-semibold mt-[2px] mb-[5px] mx-0 p-0',
                        headingColorClass[intent],
                    )}
                >
                    {title}
                </h5>
            )}
            {children}
        </div>
    )
})

export { Callout }
